import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { paymentStatus } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { devError } from "../lib/log";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const { refresh } = useAuth();
  const tries = useRef(0);

  useEffect(() => {
    let timer;
    const poll = async () => {
      if (!sessionId) { setState("failed"); return; }
      if (tries.current >= 8) { setState("failed"); return; }
      tries.current += 1;
      try {
        const s = await paymentStatus(sessionId);
        if (s.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (s.status === "failed" || s.payment_status === "failed") { setState("failed"); return; }
      } catch (e) { devError("Payment status poll failed, retrying:", e); }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#11141E] p-8 text-center" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-[#FF6A5E] mx-auto" />
            <h1 className="font-display font-bold text-xl mt-4">Confirming your payment…</h1>
            <p className="text-sm text-slate-400 mt-1">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-4" data-testid="payment-paid">You're all set! 🎉</h1>
            <p className="text-sm text-slate-400 mt-2">Your account has been upgraded. Time to create.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/studio"><Button className="rounded-full bg-[#FF453A] hover:bg-[#FF5D54] text-white font-semibold px-6" data-testid="payment-create-btn"><Crown className="h-4 w-4 mr-2" />Start creating</Button></Link>
              <Link to="/account"><Button variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5">View account</Button></Link>
            </div>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="h-12 w-12 text-red-400 mx-auto" />
            <h1 className="font-display font-bold text-xl mt-4">We couldn't confirm your payment</h1>
            <p className="text-sm text-slate-400 mt-2">If you were charged, it will reflect shortly. Otherwise please try again.</p>
            <Link to="/pricing"><Button className="mt-6 rounded-full bg-[#FF453A] hover:bg-[#FF5D54] text-white font-semibold px-6">Back to plans</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
